import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download } from 'lucide-react';
import { apiClient } from '../utils/api';
import './ReportsPage.css';
import logo from '../Logo/logo.png';

// --- Utility Functions ---

function safeJsonParse(v) {
    try {
        if (!v) return null;
        if (typeof v === 'string') return JSON.parse(v);
        if (typeof v === 'object') return v;
    } catch { }
    return null;
}

function pick(obj, keys) {
    if (!obj) return null;
    for (const k of keys) {
        const v = obj[k];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return null;
}

function normalizeMbps(v) {
    if (v === null || v === undefined || v === '') return null;
    const s = String(v).trim();
    const cleaned = s.replace(/mbps/ig, '').trim();
    return cleaned ? `${cleaned} Mbps` : null;
}

function extractTesterNotes(bug) {
    const raw = (bug.bug_description || bug.description || '').trim();
    if (!raw) return null;

    // 1. If it contains "tester feedback", isolate that part
    const idx = raw.toLowerCase().indexOf('tester feedback');
    let content = raw;
    if (idx !== -1) {
        content = raw.slice(idx).replace(/tester feedback\s*:\s*/i, '').replace(/rating\s*:\s*\d+(\.\d+)?\s*\/\s*\d+/ig, '');
    }

    // 2. Clean up common delimiters and noise
    let cleaned = content
        .replace(/[_═—-]{5,}/g, ' ')  // Long lines of underscores, equals, or dashes
        .replace(/⭐/g, '')             // Star ratings
        .replace(/\s+/g, ' ')          // Collapse whitespace
        .trim();

    // 3. If cleaning result is empty or just generic technical logs, return null
    if (!cleaned) return null;

    const tokens = ['Device:', 'Android:', 'Screen:', 'Battery:', 'Network:', 'Duration:', 'Coordinates:', 'Accuracy:', 'Address:'];
    const hits = tokens.reduce((a, t) => a + (cleaned.includes(t) ? 1 : 0), 0);
    if (hits >= 5) return null;

    return cleaned;
}

function parseScreenshots(bug) {
    const s = bug.screenshots || bug.screenshot_url || '';
    if (!s) return '';
    return String(s).split(',').map(x => x.trim()).filter(Boolean).join(' | ');
}

function getVideoUrl(bug) {
    if (!bug.recording_url) return '';
    if (bug.recording_url.startsWith('http')) return bug.recording_url;
    const base = 'https://bharatqa-backend.onrender.com';
    return base + bug.recording_url;
}

function cleanAIReport(text) {
    if (!text) return '';
    let publicText = text;
    const DELIMITER = "==== INTERNAL ADMIN VERDICT ====";
    if (text.includes(DELIMITER)) {
        publicText = text.split(DELIMITER)[0].trim();
    }
    let cleaned = publicText.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E6}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}]/gu, '');
    cleaned = cleaned.replace(/^(as a\s+[\w\s]+tester|in this report|i have audited|hi there|based on my analysis|as a Senior QA Engineer at BharatQA),?\s*/i, '');
    cleaned = cleaned.split(/final verdict:\s*\[/i)[0].trim();
    cleaned = cleaned.split(/internal reasoning:/i)[0].trim();
    const markers = ["Top 5 Fixes", "TOP 5 FIXES", "Fixes"];
    let markerFound = null;
    for (const m of markers) {
        if (cleaned.includes(m)) { markerFound = m; break; }
    }
    if (markerFound) {
        const idx = cleaned.indexOf(markerFound);
        let before = cleaned.slice(0, idx + markerFound.length);
        let after = cleaned.slice(idx + markerFound.length);
        const nextSectionIdx = after.search(/\n(?:#|\*|Severity)/i);
        let portion = nextSectionIdx !== -1 ? after.slice(0, nextSectionIdx) : after;
        let rest = nextSectionIdx !== -1 ? after.slice(nextSectionIdx) : "";
        portion = portion.replace(/\*\*/g, '');
        cleaned = before + portion + rest;
    }
    cleaned = cleaned.replace(/(?:#\s*)?SEVERITY[:\s\n]*(LOW|MEDIUM|HIGH|CRITICAL)[.\s-]*/im, '# SEVERITY: $1\n\n');
    return cleaned.trim();
}

function getSuggestedSeverity(bug) {
    const manual = (bug.severity || 'medium').toLowerCase();
    const weights = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    if (!bug.ai_analysis) return manual;
    const text = bug.ai_analysis.toUpperCase();
    const options = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
    let aiVerdict = null;
    for (const opt of options) {
        const regex = new RegExp(`(?:#\\s*)?SEVERITY[:\\s\\n]+${opt}`, 'i');
        if (regex.test(text)) {
            aiVerdict = opt.toLowerCase();
            break;
        }
    }
    if (aiVerdict && weights[aiVerdict] > weights[manual]) return aiVerdict;
    return manual;
}

function exportToCSV(reports, company) {
    if (reports.length === 0) return;

    const headers = [
        'Issue Title', 'Project', 'Severity', 'Device', 'OS Version', 'RAM (MB Free)', 'Storage (MB Free)', 'Screen Type', 'Network', 'Network Speed',
        'Battery Drain', 'Duration', 'Location (City)', 'Date', 'Time (IST)', 'Status', 'AI Analysis', 'Tester Notes', 'Steps to Reproduce',
        'Expected Result', 'Actual Result', 'Media (Video)', 'Screenshots'
    ];

    const escape = (val) => {
        if (val == null) return '';
        const str = String(val).replace(/"/g, '""').replace(/\r?\n/g, ' ');
        return `"${str}"`;
    };

    const rows = reports.map(r => {
        const stats = safeJsonParse(r.device_stats);
        const deviceModel = pick(stats, ['device_model', 'deviceModel', 'model']) || r.tester_device || r.device_model || '';
        const androidVersion = pick(stats, ['android_version', 'androidVersion', 'osVersion']) || r.tester_os || r.os_version || '';
        const screenRes = pick(stats, ['screen_resolution', 'screenResolution', 'resolution']) || r.tester_res || '';
        const ramMb = pick(stats, ['availableMemoryMb']) ? `${pick(stats, ['availableMemoryMb'])} MB` : (pick(stats, ['ram_gb', 'ram', 'memory', 'totalRam', 'total_ram']) || r.tester_ram || '');
        const storageMb = pick(stats, ['availableStorageMb']) ? `${pick(stats, ['availableStorageMb'])} MB` : '';
        const networkType = pick(stats, ['network_type', 'networkType']) || r.tester_net || r.network_type || '';
        const networkSpeed = pick(stats, ['network_speed_mbps', 'networkSpeedMbps', 'networkSpeed']);
        const batteryDrain = pick(stats, ['battery_drain', 'batteryDrain']);
        const durationSec = pick(stats, ['duration_seconds', 'testDuration', 'durationSeconds', 'duration']) || r.test_duration || '';
        const locationCity = pick(stats, ['city']) || r.tester_city || '';

        const aiAnalysisText = typeof r.ai_analysis === 'string'
            ? r.ai_analysis
            : r.ai_analysis ? JSON.stringify(r.ai_analysis) : '';
        
        const istTime = r.created_at ? new Date(r.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '';

        return [
            escape(r.bug_title || 'Untitled Issue'), escape(r.app_name || ''),
            escape(r.severity || ''), escape(deviceModel), escape(androidVersion), escape(ramMb), escape(storageMb), escape(screenRes), escape(networkType),
            escape(normalizeMbps(networkSpeed) || ''), escape(batteryDrain != null ? `${batteryDrain}%` : ''),
            escape(durationSec != null ? `${durationSec}s` : ''), escape(locationCity),
            escape(r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''),
            escape(istTime),
            escape(r.ai_analysis ? 'Analyzed' : 'New'), escape(aiAnalysisText), escape(extractTesterNotes(r) || ''),
            escape(r.steps_to_reproduce || ''), escape(r.expected_result || ''), escape(r.actual_result || ''),
            escape(getVideoUrl(r)), escape(parseScreenshots(r))
        ];
    });

    const csvContent = [headers.map(h => `"${h}"`).join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `bharatqa-report-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// --- Main Component ---

export default function ReportsPage({ company, showToast }) {
    const [reports, setReports] = useState([]);
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [showSkeleton, setShowSkeleton] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [downloadingBug, setDownloadingBug] = useState(null);

    const loadData = useCallback(async () => {
        let isActive = true;
        setLoading(true);
        setFetchError(false);
        setShowSkeleton(true);

        const minDelay = 200 + Math.random() * 200;
        const minDelayPromise = new Promise(resolve => setTimeout(resolve, minDelay));

        const fetchData = async () => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            try {
                const data = await apiClient.getTests(company.id);
                setTests(data);

                const allBugsPromises = data.map(async (test) => {
                    const bugs = await apiClient.getBugs(test.id);
                    return bugs.map(b => ({ 
                        ...b, 
                        testContext: test,
                        app_name: b.app_name || test.app_name // Ensure app_name is available for PDF
                    }));
                });
                const allBugs = await Promise.all(allBugsPromises);
                const flattenedBugs = allBugs.flat();
                setReports(flattenedBugs);

                clearTimeout(timeoutId);
            } catch (err) {
                clearTimeout(timeoutId);
                throw err;
            }
        };

        try {
            await Promise.all([fetchData(), minDelayPromise]);
            if (isActive) setShowSkeleton(false);
        } catch (err) {
            console.error(err);
            if (isActive) {
                setFetchError(true);
                await minDelayPromise;
                setShowSkeleton(false);
            }
        } finally {
            if (isActive) setLoading(false);
        }

        return () => { isActive = false; };
    }, [company.id]); // Removed showToast if not needed, but can add back if requested.

    useEffect(() => { loadData(); }, [loadData]);



    const handleExport = () => {
        if (reports.length === 0) {
            showToast('No data to export yet', 'error');
            return;
        }
        setExporting(true);
        try {
            exportToCSV(reports, company);
            showToast(`✅ Exported ${reports.length} issue${reports.length !== 1 ? 's' : ''} to CSV`);
        } catch (err) {
            showToast('Export failed', 'error');
        } finally {
            setTimeout(() => setExporting(false), 800);
        }
    };

    // PDF Logic reuse
    const generatePDF = async (bug, testCtx) => {
        try {
            const loadScript = (src) => new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) return resolve();
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'mm', 'a4');
            const w = doc.internal.pageSize.getWidth();
            const h = doc.internal.pageSize.getHeight();
            const m = 20;

            let y = m;

            const drawPageBase = () => {
                doc.setFillColor('#f9fafb');
                doc.rect(0, 0, w, h, 'F');
                const headerY = m;
                doc.addImage(logo, 'PNG', 14, 6, 10, 10);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                doc.setTextColor('#111827');
                doc.text('BharatQA', m + 10, 10);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor('#6b7280');
                const confText = 'Confidential — Security Audit Report';
                doc.text(confText, w - m - doc.getTextWidth(confText), headerY);
                doc.setDrawColor('#e5e7eb');
                doc.setLineWidth(0.35);
                doc.line(m, headerY + 2, w - m, headerY + 2);
            };

            const checkSpace = (requiredSpace, targetY) => {
                const checkY = targetY || y;
                if (checkY + requiredSpace > h - 15) {
                    doc.addPage();
                    drawPageBase();
                    y = m + 8;
                    return true;
                }
                return false;
            };

            drawPageBase();
            y = m + 8;

            const blockHeight = 42;
            doc.setFillColor('#ffffff');
            doc.setDrawColor('#e5e7eb');
            doc.roundedRect(m, y, w - m * 2, blockHeight, 3, 3, 'FD');

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor('#6b7280');
            doc.text('Official Audit Report', m + 8, y + 10);

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(22);
            doc.setTextColor('#111827');
            doc.text(testCtx?.app_name || bug.app_name || 'Project', m + 8, y + 20);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor('#6b7280');
            doc.text(`${company?.name || 'Company'} · ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`, m + 8, y + 30);

            const pillW = 16;
            const pillH = 6;
            const pillX = w - m - 8 - pillW;
            const pillY = y + 8;
            doc.setFillColor(22, 101, 52);
            doc.roundedRect(pillX, pillY, pillW, pillH, 1.5, 1.5, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(220, 252, 231);
            doc.text('Active', pillX + pillW / 2, pillY + 4, { align: 'center' });

            y += blockHeight + 6;

            const cardW = (w - m * 2 - 4) / 2;
            const leftCardX = m;
            const rightCardX = m + cardW + 4;

            const scopeText = testCtx?.instructions || "No specific instructions were provided for this single-issue extraction report. This report is a direct export of a single finding.";
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            const sLines = doc.splitTextToSize(scopeText, cardW - 12);
            const scopeLinesHeight = sLines.length * 9 * 0.3527 * 1.5;
            const cardsHeight = Math.max(12 + scopeLinesHeight, 72);

            doc.setFillColor('#ffffff');
            doc.setDrawColor('#e5e7eb');
            doc.roundedRect(leftCardX, y, cardW, cardsHeight, 2, 2, 'FD');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor('#6b7280');
            doc.text('Project scope', leftCardX + 6, y + 8);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor('#374151');
            doc.setLineHeightFactor(1.5);
            doc.text(sLines, leftCardX + 6, y + 14);

            doc.setFillColor('#ffffff');
            doc.roundedRect(rightCardX, y, cardW, cardsHeight, 2, 2, 'FD');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor('#6b7280');
            doc.text('Audit targets', rightCardX + 6, y + 8);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            const stats = safeJsonParse(bug.device_stats);
            const istTime = bug.created_at ? new Date(bug.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '';
            const labels = ['Device', 'OS', 'RAM', 'Storage', 'Thermals', 'Network', 'Res', 'Battery', 'Loc', 'Time (IST)'];
            const vals = [
                pick(stats, ['device_model', 'model']) || bug.tester_device || 'Unknown',
                pick(stats, ['android_version', 'osVersion']) || bug.tester_os || 'Gen 10',
                pick(stats, ['availableMemoryMb']) ? `${Math.round(stats.availableMemoryMb)} MB` : (pick(stats, ['ram_gb', 'ram', 'memory']) || bug.tester_ram || '?') + ' GB',
                pick(stats, ['availableStorageMb']) ? `${Math.round(stats.availableStorageMb / 1024)} GB` : '?',
                stats?.highestThermalCelsius ? `${stats.highestThermalCelsius}°C (${stats.highestThermalState || ''})` : (stats?.highestThermalState || 'N/A'),
                (pick(stats, ['network_type', 'networkType']) || bug.tester_net || 'WIFI').toUpperCase() + (pick(stats, ['network_speed_mbps', 'networkSpeedMbps']) ? ` (${pick(stats, ['network_speed_mbps', 'networkSpeedMbps'])} Mbps)` : ''),
                pick(stats, ['screen_resolution', 'resolution']) || bug.tester_res || '720x1280',
                (pick(stats, ['battery_drain', 'batteryDrain']) != null ? `${pick(stats, ['battery_drain', 'batteryDrain'])}%` : 'N/A'),
                pick(stats, ['city']) || bug.tester_city || 'India',
                istTime
            ];

            let ty = y + 14;
            labels.forEach((lbl, i) => {
                doc.setFont('helvetica', 'normal');
                doc.setTextColor('#6b7280');
                doc.text(lbl, rightCardX + 6, ty);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor('#111827');
                doc.text(String(vals[i]), rightCardX + cardW - 6, ty, { align: 'right' });
                ty += 5.5;
            });

            y += cardsHeight + 10;

            // --- NEW: Executive Summary Page ---
            if (testCtx?.ai_summary) {
                doc.addPage();
                drawPageBase();
                y = m + 8;

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(18);
                doc.setTextColor('#111827');
                doc.text('Executive Summary', m, y);
                y += 10;

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor('#374151');
                doc.setLineHeightFactor(1.6);

                const summaryLines = doc.splitTextToSize(testCtx.ai_summary.replace(/#+\s/g, ''), w - m * 2);

                for (let line of summaryLines) {
                    if (y > h - 25) {
                        doc.addPage();
                        drawPageBase();
                        y = m + 8;
                    }
                    doc.text(line, m, y);
                    y += 6;
                }

                y += 10;
                doc.setDrawColor('#e5e7eb');
                doc.setLineWidth(0.5);
                doc.line(m, y, w - m, y);
                y += 15;
            }

            // --- RENDER FINDING CONTENT DIRECTLY (No DOM Scraping) ---
            const markdown = cleanAIReport(bug.ai_analysis);
            if (markdown) {
                checkSpace(40);
                doc.setLineWidth(0.15);
                doc.setDrawColor('#e5e7eb');
                doc.line(m, y, w - m, y);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                doc.setTextColor('#9ca3af');
                const divText = `Finding Export`;
                const tW = doc.getTextWidth(divText);
                doc.setFillColor('#f9fafb');
                doc.rect(w / 2 - tW / 2 - 2, y - 2, tW + 4, 4, 'F');
                doc.text(divText, w / 2, y + 1, { align: 'center' });
                y += 8;

                const cardMargin = m + 4;
                const rawLines = markdown.split('\n');
                let cy = y;

                // Pre-process: sanitize unicode that jsPDF can't render (causes wide character spacing)
                const lines = [];
                for (let ri = 0; ri < rawLines.length; ri++) {
                    let line = rawLines[ri].trim();
                    line = line.replace(/[\uFF01-\uFF5E]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0));
                    line = line.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"');
                    line = line.replace(/[\u2013\u2014]/g, '-').replace(/\u2026/g, '...');
                    line = line.replace(/[^\x00-\x7F]/g, ch => {
                        if (ch.charCodeAt(0) >= 0x00A0 && ch.charCodeAt(0) <= 0x00FF) return ch;
                        return '';
                    });
                    line = line.replace(/\*\*/g, '').replace(/__/g, '');
                    lines.push(line);
                }

                const loadImageAsBase64 = async (url) => {
                  try {
                    const response = await fetch(url);
                    if (!response.ok) return null;
                    const blob = await response.blob();
                    return new Promise((resolve) => {
                      const reader = new FileReader();
                      reader.onloadend = () => resolve(reader.result);
                      reader.readAsDataURL(blob);
                    });
                  } catch (e) { return null; }
                };

                let globalImageBottom = 0;
                let inFrameZone = true;
                let narrowForImage = false;
                const LH = 1.5;
                const fullTextW = w - m * 2 - 4;
                const imgW = 25;
                const imgH = 45;
                const narrowTextW = fullTextW - imgW - 8;

                for (let i = 0; i < lines.length; i++) {
                    const line = lines[i];
                    let trimmed = line.trim();
                    if (!trimmed) { cy += 3; continue; }

                    trimmed = trimmed.replace(/^- |^\* |^\d+\.\s/, '');

                    const match = trimmed.match(/\[Frame\s+(\d+)\]/i);
                    let imgBase64 = null;
                    
                    const isFrameLine = match && (trimmed.includes('Frame & Timestamp') || trimmed.includes('Frame &amp; Timestamp'));

                    if (isFrameLine) {
                        const paddedNum = String(parseInt(match[1], 10)).padStart(4, '0');
                        const API = window.location.origin.includes('localhost') ? 'http://localhost:5000/api' : 'https://bharatqa-backend.onrender.com/api';
                        const imgUrl = `${API}/debug-frames/bug-${bug.id}/frame_${paddedNum}.png`;
                        imgBase64 = await loadImageAsBase64(imgUrl);
                        narrowForImage = !!imgBase64;
                    }

                    const isAnyHeader = trimmed.startsWith('# ') || trimmed.startsWith('## ') || trimmed.startsWith('### ');
                    const isSeverityLine = trimmed.toUpperCase().includes('SEVERITY:');

                    if (isSeverityLine || isAnyHeader) {
                        narrowForImage = false;
                        inFrameZone = !isAnyHeader || /OBSERVED/i.test(trimmed);
                        globalImageBottom = 0;
                    }

                    let currentTextW = (narrowForImage || (inFrameZone && cy < globalImageBottom)) ? narrowTextW : fullTextW;

                    doc.setLineHeightFactor(LH);
                    const dynamicLines = doc.splitTextToSize(trimmed, currentTextW);
                    let nodeHeightRequired = dynamicLines.length * 8 * 0.3527 * LH + 5;
                    if (imgBase64) nodeHeightRequired = Math.max(nodeHeightRequired, imgH + 6);
                    if (isFrameLine) nodeHeightRequired += 8;
                    if (isAnyHeader) nodeHeightRequired += 10;

                    if (checkSpace(nodeHeightRequired, cy)) {
                        cy = y;
                        globalImageBottom = 0;
                    }

                    if (isFrameLine) {
                        cy += 4;
                        doc.setDrawColor('#e5e7eb');
                        doc.setLineWidth(0.2);
                        doc.line(cardMargin, cy, w - m - 6, cy);
                        cy += 4;
                    }

                    if (isSeverityLine) {
                        const sevMatch = trimmed.match(/SEVERITY:\s*(LOW|MEDIUM|HIGH|CRITICAL)/i);
                        const sev = sevMatch ? sevMatch[1].toUpperCase() : 'MEDIUM';
                        doc.setFont('helvetica', 'bold');
                        doc.setFontSize(8);
                        doc.setTextColor('#374151');
                        doc.text(`Severity: ${sev}`, cardMargin, cy + 4);
                        cy += 8;

                    } else if (trimmed.startsWith('# ')) {
                        cy += 6;
                        doc.setDrawColor('#d1d5db');
                        doc.setLineWidth(0.3);
                        doc.line(cardMargin, cy, w - m - 6, cy);
                        cy += 6;
                        const txt = trimmed.replace(/^#+\s+/, '').toUpperCase();
                        doc.setFont('helvetica', 'bold');
                        doc.setFontSize(11);
                        doc.setTextColor('#111827');
                        doc.text(txt, cardMargin, cy + 4);
                        cy += 10;

                    } else if (trimmed.startsWith('## ') || trimmed.startsWith('### ')) {
                        cy += 5;
                        let txt = trimmed.replace(/^#+\s+/, '');
                        doc.setFont('helvetica', 'bold');
                        doc.setFontSize(10);
                        doc.setTextColor('#111827');
                        doc.text(txt, cardMargin, cy + 4);
                        cy += 9;

                    } else {
                        doc.setFont('helvetica', isFrameLine ? 'bold' : 'normal');
                        doc.setFontSize(8);
                        doc.setTextColor(isFrameLine ? '#111827' : '#374151');

                        if (imgBase64) {
                            const imgX = w - m - imgW - 6;
                            const API_BASE = window.location.origin.includes('localhost') ? 'http://localhost:5000' : 'https://bharatqa-backend.onrender.com';
                            const videoUrl = bug.recording_url?.startsWith('http') ? bug.recording_url : `${API_BASE}${bug.recording_url}`;
                            
                            doc.addImage(imgBase64, imgX, cy + 1, imgW, imgH);
                            doc.link(imgX, cy + 1, imgW, imgH, { url: videoUrl });
                            
                            globalImageBottom = cy + 1 + imgH;
                            doc.text(dynamicLines, cardMargin, cy + 3);
                            cy += dynamicLines.length * 8 * 0.3527 * LH + 4;
                        } else {
                            doc.text(dynamicLines, cardMargin, cy + 3);
                            cy += dynamicLines.length * 8 * 0.3527 * LH + 4;
                        }
                    }
                }
                y = cy;
            }

            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                const fY = h - 10;
                doc.setDrawColor('#e5e7eb');
                doc.setLineWidth(0.15);
                doc.line(m, fY - 4, w - m, fY - 4);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                doc.setTextColor('#9ca3af');
                doc.text("© 2026 BharatQA Security Portal. All audit findings are classified and confidential.", m, fY);
                const pTxt = `Page ${i} of ${totalPages}`;
                doc.text(pTxt, w - m - doc.getTextWidth(pTxt), fY);
            }

            doc.save(`BharatQA-Export-${bug.id || 'Issue'}.pdf`);
        } catch (e) {
            console.error(e);
            showToast("Failed to generate PDF", "error");
        }
    };

    const handleDownloadClick = (bug) => {
        setDownloadingBug(bug);
        setTimeout(() => {
            const testCtx = bug.testContext;
            generatePDF(bug, testCtx);
            setDownloadingBug(null);
        }, 500);
    };

    const totalBugs = reports.length;
    const criticalBugs = reports.filter(r => (r.severity || '').toLowerCase() === 'critical').length;
    const analyzedReports = reports.filter(r => r.ai_analysis).length;

    const renderSkeletons = () => (
        <>
            {[0, 0.15, 0.3, 0.45].map((delay, idx) => (
                <tr className="skel-row" key={idx}>
                    <td><div className="skel-bar skel-td-title" style={{ animationDelay: `${delay}s` }}></div></td>
                    <td><div className="skel-bar skel-td-project" style={{ animationDelay: `${delay}s` }}></div></td>
                    <td><div className="skel-bar skel-td-reporter" style={{ animationDelay: `${delay}s` }}></div></td>
                    <td><div className="skel-bar skel-td-date" style={{ animationDelay: `${delay}s` }}></div></td>
                    <td><div className="skel-bar skel-td-status" style={{ animationDelay: `${delay}s` }}></div></td>
                    <td></td>
                </tr>
            ))}
        </>
    );

    return (
        <div className="reports-page">
            <header className="rp-header-container">
                <div className="rp-header-content">
                    <h1>Reports</h1>
                </div>
                <button
                    className={`rp-export-btn ${exporting ? 'exporting' : ''}`}
                    onClick={handleExport}
                    disabled={exporting || showSkeleton}
                >
                    {exporting ? (
                        <>
                            <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                            <span>Exporting…</span>
                        </>
                    ) : (
                        <>
                            <Download size={14} />
                            <span>Export CSV</span>
                        </>
                    )}
                </button>
            </header>

            <div className="section-label">Performance overview</div>
            <div className="rp-stats-grid">
                {showSkeleton ? (
                    <>
                        <div className="rp-stat-card">
                            <div className="skel-stat-label" />
                            <div className="skel-stat-number" />
                            <div className="skel-stat-desc" />
                        </div>
                        <div className="rp-stat-card">
                            <div className="skel-stat-label" />
                            <div className="skel-stat-number" />
                            <div className="skel-stat-desc" />
                        </div>
                        <div className="rp-stat-card">
                            <div className="skel-stat-label" />
                            <div className="skel-stat-number" />
                            <div className="skel-stat-desc" />
                        </div>
                    </>
                ) : fetchError ? (
                    <div className="rp-error-row" style={{ gridColumn: '1 / -1' }}>
                        <span className="rp-error-icon">⚠</span>
                        Failed to load — <button className="rp-retry-link" onClick={loadData}>Retry</button>
                    </div>
                ) : (
                    <>
                        <div className="rp-stat-card">
                            <span className="rp-stat-label">Total issues</span>
                            <div className="rp-stat-value">{totalBugs}</div>
                            <span className="rp-stat-desc">across all projects</span>
                        </div>

                        <div className={`rp-stat-card ${criticalBugs > 0 ? 'danger' : 'critical'}`}>
                            <span className="rp-stat-label">Critical bugs</span>
                            <div className="rp-stat-value">{criticalBugs}</div>
                            <span className="rp-stat-desc">requiring immediate fix</span>
                        </div>

                        <div className="rp-stat-card">
                            <span className="rp-stat-label">Ai analyzed</span>
                            <div className="rp-stat-value">{analyzedReports}</div>
                            <span className="rp-stat-desc">reports processed</span>
                        </div>
                    </>
                )}
            </div>

            <div className="rp-content-section">
                <div className="rp-main-card">
                    <div className="rp-card-header">
                        {showSkeleton ? (
                            <>
                                <div className="skel-header-bar" />
                                <div className="skel-badge-bar" />
                            </>
                        ) : fetchError ? (
                            <h2 className="rp-card-title">All Reported Issues</h2>
                        ) : (
                            <>
                                <h2 className="rp-card-title">All Reported Issues</h2>
                                {reports.length > 0 && <span className="rp-count-badge">{reports.length} total</span>}
                            </>
                        )}
                    </div>

                    <div className="rp-table-wrapper">
                        <table className="rp-table">
                            <thead>
                                {showSkeleton ? (
                                    <tr>
                                        <th><div className="skel-th" style={{ width: 120 }}></div></th>
                                        <th></th>
                                        <th><div className="skel-th" style={{ width: 72 }}></div></th>
                                        <th><div className="skel-th" style={{ width: 48 }}></div></th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th>Issue title</th>
                                        <th>Project</th>
                                        <th>Reporter</th>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {showSkeleton ? renderSkeletons() : fetchError ? (
                                    <tr>
                                        <td colSpan="6">
                                            <div className="rp-error-row">
                                                <span className="rp-error-icon">⚠</span>
                                                Failed to load — <button className="rp-retry-link" onClick={loadData}>Retry</button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : reports.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                                            No reports found.
                                        </td>
                                    </tr>
                                ) : (
                                    reports.map(report => {
                                        const initials = (report.app_name || 'Project').substring(0, 2).toUpperCase();
                                        return (
                                            <tr key={report.id}>
                                                <td className="font-medium">{report.title || report.bug_title || 'Untitled Issue'}</td>
                                                <td>
                                                    <div className="project-tag">
                                                        <span className="project-monogram">{initials}</span>
                                                        <span className="project-name">{report.app_name}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="reporter-info">
                                                        {report.tester_name || 'Anonymous'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="rp-date">
                                                        {new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </div>
                                                </td>
                                                <td>
                                                    {report.ai_analysis
                                                        ? <span className="status-pill analyzed">Analyzed</span>
                                                        : <span className="status-pill new">New</span>
                                                    }
                                                </td>
                                                <td>
                                                    <span className="pdf-link" onClick={() => handleDownloadClick(report)}>
                                                        Download PDF →
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Hidden export template logic */}
            {downloadingBug && (
                <div style={{ position: 'absolute', top: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                    <div className={`hidden-finding-card-export-${downloadingBug.id}`}>
                        <div className="bug-basics">
                            <h3>{downloadingBug.title || downloadingBug.bug_title || 'Internal Finding'}</h3>
                            <span className="pill-tester">{downloadingBug.tester_name || 'Expert Auditor'}</span>
                        </div>
                        <span className="pill-severity">{getSuggestedSeverity(downloadingBug).charAt(0).toUpperCase() + getSuggestedSeverity(downloadingBug).slice(1).toLowerCase()}</span>
                        {downloadingBug.ai_analysis && (
                            <div className="markdown-ai">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        h1: ({ node, ...props }) => {
                                            const text = String(props.children);
                                            if (text.toUpperCase().includes('SEVERITY:')) {
                                                const sev = text.split(':')[1]?.trim() || '';
                                                return <div className="markdown-severity-row">{sev}</div>;
                                            }
                                            return <h1 {...props} />;
                                        },
                                        h2: ({ node, ...props }) => {
                                            const t = String(props.children).toUpperCase();
                                            if (t.includes('BUG REPRODUCTION STEPS')) return <h2>Bug reproduction steps</h2>;
                                            if (t.includes('ROOT CAUSE')) return <h2>Technical root cause</h2>;
                                            if (t.includes('TOP 5 FIXES') || t.includes('FIXES')) return <h2>Top 5 fixes</h2>;
                                            return <h2 {...props} />;
                                        },
                                        h3: ({ node, ...props }) => {
                                            const t = String(props.children).toUpperCase();
                                            if (t.includes('BUG REPRODUCTION STEPS')) return <h3>Bug reproduction steps</h3>;
                                            if (t.includes('ROOT CAUSE')) return <h3>Technical root cause</h3>;
                                            if (t.includes('TOP 5 FIXES') || t.includes('FIXES')) return <h3>Top 5 fixes</h3>;
                                            return <h3 {...props} />;
                                        },
                                        ul: ({ node, ...props }) => <ul {...props} />,
                                        ol: ({ node, ...props }) => <ol {...props} />
                                    }}
                                >
                                    {cleanAIReport(downloadingBug.ai_analysis)}
                                </ReactMarkdown>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
